import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import * as API from '../api';
import ShortcutButton from '../components/ShortcutButton';
import { useAppBar } from '../context/AppBar';
import CalendarIcon from '../resources/img/calendar.svg';
import ProfileIcon from '../resources/img/profile.svg';
import { useAppContainer } from '../context/AppContainer';
import SafeLifeSpin from '../components/SafeLifeSpin';
import { useUser } from '../context/user';
import PotentialCourseInstanceCard from '../components/PotentialCourseInstanceCard';
import { useCalendar } from '../context/Calendar';
import { CourseInstanceCardData } from '../types/CourseInstance';
import CourseInstanceCard from '../components/CourseInstanceCard';
import OpenClassSessionCard from '../components/OpenClassSessionCard';
import { MobileOpenClassSessionSummary } from '../types/OpenClassSession';

const Start: React.FC = () => {
  const [courseInstances, setCourseInstances] = useState<
    CourseInstanceCardData[]
  >([]);
  const [openClassSessions, setOpenClassSessions] = useState<
    MobileOpenClassSessionSummary[]
  >([]);
  const [potentialCourseInstanceCount, setPotentialCourseInstanceCount] =
    useState<number>();
  const [loadingCourseInstances, setLoadingCourseInstances] = useState(true);
  const [loadingOpenClassSessions, setLoadingOpenClassSessions] =
    useState(true);
  const [loadingPotentialCourseInstances, setLoadingPotentialCourseInstances] =
    useState(true);
  const navigate = useNavigate();
  const [, setAppBarInterface] = useAppBar();
  const [, setCurrentClasses] = useAppContainer();
  const [, setCalendarState] = useCalendar();
  const { t } = useTranslation();

  const currentUser = useUser()[0];

  useEffect(() => {
    setAppBarInterface({ title: t('common.start') });
  }, [i18next.language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentClasses('bg-white');
    fetchCourseInstances();
    fetchOpenClassSessions();
    fetchPotentialCourseInstanceCount();
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const potentialCourseInstancesCardClick = (month: Dayjs) => {
    setCalendarState({
      displayedMonth: month,
      selectedDates: { start: null, end: null },
      selectedCourseInstancesCount: 0,
    });
    navigate('/kalender');
  };

  const fetchCourseInstances = async () => {
    if (currentUser?.id == null) {
      setLoadingCourseInstances(false);
      return;
    }
    setLoadingCourseInstances(true);
    try {
      const today = dayjs().startOf('day');
      await API.getCourseInstances({
        startsAt: today.toISOString(),
        endsAt: today.add(7, 'days').toISOString(),
        instructorId: currentUser.id,
      }).then((response) => {
        if (response.status == 200) {
          setCourseInstances(response.data.data);
        }
      });
    } finally {
      setLoadingCourseInstances(false);
    }
  };

  const fetchOpenClassSessions = async () => {
    if (currentUser?.id == null) {
      setLoadingOpenClassSessions(false);
      return;
    }
    setLoadingOpenClassSessions(true);
    try {
      const today = dayjs().startOf('day');
      const { data } = await API.getOpenClassSessions({
        from: today.toISOString(),
        to: today.add(7, 'days').toISOString(),
        instructorId: currentUser.id,
      });
      setOpenClassSessions(data);
    } finally {
      setLoadingOpenClassSessions(false);
    }
  };

  const fetchPotentialCourseInstanceCount = async () => {
    if (currentUser?.id == null) {
      setLoadingPotentialCourseInstances(false);
      return;
    }
    setLoadingPotentialCourseInstances(true);
    try {
      const today = dayjs().startOf('day');
      const { data } = await API.getPotentialCourseInstanceCount({
        instructorId: currentUser.id,
        startDate: today.format('YYYY-MM-DD'),
        endDate: today.add(1, 'year').format('YYYY-MM-DD'),
      });
      setPotentialCourseInstanceCount(data);
    } finally {
      setLoadingPotentialCourseInstances(false);
    }
  };

  const upcoming = [
    ...courseInstances.map((courseInstance) => ({
      key: `course-instance-${courseInstance.id}`,
      startsAt: courseInstance.startsAt,
      card: (
        <CourseInstanceCard
          courseInstance={courseInstance}
          onAccept={() => fetchCourseInstances()}
        />
      ),
    })),
    ...openClassSessions.map((session) => ({
      key: `open-class-session-${session.id}`,
      startsAt: session.startAt,
      card: <OpenClassSessionCard session={session} />,
    })),
  ].sort(
    (first, second) =>
      dayjs(first.startsAt).valueOf() - dayjs(second.startsAt).valueOf(),
  );

  return (
    <div className="p-4 view-max-width">
      <div className="flex justify-between gap-1 px-1 py-1">
        <ShortcutButton
          text={t('common.calendar')}
          onClick={() => navigate('/kalender')}
          icon={<img src={CalendarIcon} />}
        />
        <div className="w-4"></div>
        <ShortcutButton
          text={t('common.profile')}
          onClick={() => navigate('/profil')}
          icon={<img src={ProfileIcon} className="pt-1" />}
        />
      </div>
      <div className="flex mt-4 justify-center flex-col">
        {potentialCourseInstanceCount !== 0 && (
          <PotentialCourseInstanceCard
            start={dayjs()}
            end={dayjs().endOf('month')}
            potentialCount={potentialCourseInstanceCount}
            loading={loadingPotentialCourseInstances}
            onClick={() => potentialCourseInstancesCardClick(dayjs())}
          />
        )}
      </div>
      <div className="text-2xl font-semibold mt-6 mb-2">
        {t('common.courseInstances')}
      </div>
      <div className="text-blueGrayMediumDark mb-1">
        {t('views.Start.confirmedSevenDays')}
      </div>
      <div className="border-solid border-0 border-t-1 border-grayLight mb-3" />
      <div>
        {loadingCourseInstances || loadingOpenClassSessions ? (
          <div className="flex justify-center items-center mt-14">
            <SafeLifeSpin size="large" />
          </div>
        ) : upcoming.length == 0 ? (
          <div className="text-blueGrayMediumDark font-normal text-sm py-4 rounded-lg bg-white flex-1 flex flex-col items-center shadow-md border-solid border-1 border-grayLight">
            {t('views.Start.noConfirmedSevenDays')}
          </div>
        ) : (
          upcoming.map(({ key, card }) => (
            <React.Fragment key={key}>{card}</React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default Start;
